import React, { useContext, useEffect, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout/Layout';
import { APP_NAME } from './config';
import { AuthContext } from './auth/AuthContext';
import { CapabilitiesProvider } from './auth/CapabilitiesContext';
import { AlertProvider } from './alerts/AlertContext';
import { TagEventsProvider } from './contexts/TagEventsContext';
import { ConnectionHealthProvider } from './contexts/ConnectionHealthContext';
import { I18nProvider } from './i18n/I18nProvider';
import LoginPage from './pages/LoginPage';
import routeConfig from './routes/routeConfig';
import RequireCapability from './auth/RequireCapability';
import CommandPalette from './components/CommandPalette/CommandPalette';
import SupportWidget from './components/SupportWidget/SupportWidget';
import frontendTelemetryService from './services/frontend-telemetry-service';

// Maps route paths to translation keys for document.title and screen reader announcements
const ROUTE_TITLE_KEYS = {
  '/': 'home:routeTitles.dashboard',
  '/devices': 'home:routeTitles.devices',
  '/device-groups': 'home:routeTitles.deviceGroups',
  '/metrics': 'home:routeTitles.metrics',
  '/monitoring': 'home:routeTitles.monitoring',
  '/firmwares': 'home:routeTitles.firmwares',
  '/provisioning/onboard': 'home:routeTitles.deviceOnboarding',
  '/provisioning-templates': 'home:routeTitles.provisioningTemplates',
  '/device-configurations': 'home:routeTitles.deviceConfigurations',
  '/device-configurations/bulk': 'home:routeTitles.bulkConfiguration',
  '/provisioning/approvals': 'home:routeTitles.deviceApprovals',
  '/provisioning/claim-tokens': 'home:routeTitles.claimTokens',
  '/provisioning/certificates': 'home:routeTitles.certificates',
  '/provisioning/whitelist': 'home:routeTitles.whitelist',
  '/provisioning/events': 'home:routeTitles.provisioningEvents',
  '/provisioning': 'home:routeTitles.provisioning',
  '/api-keys': 'home:routeTitles.apiKeys',
  '/webhooks': 'home:routeTitles.webhooks',
  '/external-api-messages': 'home:routeTitles.externalApiMessages',
  '/commands': 'home:routeTitles.commands',
  '/device-logs': 'home:routeTitles.deviceLogs',
  '/tasks': 'home:routeTitles.tasks',
  '/events': 'home:routeTitles.events',
  '/mqtt-control': 'home:routeTitles.mqttControl',
  '/reference-lists': 'home:routeTitles.referenceLists',
  '/analytics/health': 'home:routeTitles.healthAnalytics',
  '/analytics/status': 'home:routeTitles.statusAnalytics',
  '/analytics/tags': 'home:routeTitles.tagAnalytics',
  '/analytics/gpi': 'home:routeTitles.gpiAnalytics',
  '/analytics/connectivity': 'home:routeTitles.connectivity',
  '/analytics/commands': 'home:routeTitles.commandAnalytics',
  '/analytics/boot': 'home:routeTitles.bootMonitoring',
  '/analytics/analysis': 'home:routeTitles.analysis',
  '/analysis-reports': 'home:routeTitles.analysisReports',
  '/reports/productivity': 'home:routeTitles.productivityReports',
  '/reports/unique-items': 'home:routeTitles.uniqueItemsReport',
  '/device-locks': 'home:routeTitles.deviceLocks',
  '/users': 'home:routeTitles.users',
  '/discrepancies': 'home:routeTitles.discrepancies',
  '/divergence-logs': 'home:routeTitles.divergenceLogs',
  '/stock-movements': 'home:routeTitles.stockMovements',
  '/unauthorized': 'home:routeTitles.unauthorized',
};

function FocusOnRouteChange() {
  const location = useLocation();
  const { t } = useTranslation(['home']);
  useEffect(() => {
    const title = t(ROUTE_TITLE_KEYS[location.pathname] || 'home:routeTitles.page');
    document.title = `${title} — ${APP_NAME}`;
    const main = document.getElementById('main-content');
    if (main) main.focus();
  }, [location.pathname, t]);
  return null;
}

function AppContent() {
  const { role, login } = useContext(AuthContext);

  useEffect(() => {
    if (!role) return undefined;

    const handleDevicesFirstAction = (event) => {
      const detail = event && event.detail ? event.detail : {};
      frontendTelemetryService.trackDevicesFirstAction(detail).catch(() => {
        // Best-effort telemetry; intentionally no UI error.
      });
    };

    window.addEventListener('devices:first-action', handleDevicesFirstAction);
    return () => window.removeEventListener('devices:first-action', handleDevicesFirstAction);
  }, [role]);

  const handleLogin = async (username, password) => {
    try {
      await login(username, password);
    } catch (error) {
      throw error;
    }
  };

  if (!role) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <ToastContainer />
      </>
    );
  }

  // Return authenticated app with providers that require auth (sockets)
  return (
    <CapabilitiesProvider>
    <AlertProvider>
      <TagEventsProvider autoSubscribeAll={true}>
        <ConnectionHealthProvider>
          <FocusOnRouteChange />
          <Layout>
            <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
              <Routes>
                {routeConfig.map(({ path, element, requiredRoles, capability }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <RequireCapability capability={capability} roles={requiredRoles}>
                        {element}
                      </RequireCapability>
                    }
                  />
                ))}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
          <CommandPalette />
          <SupportWidget />
          <ToastContainer />
        </ConnectionHealthProvider>
      </TagEventsProvider>
    </AlertProvider>
    </CapabilitiesProvider>
  );
}

function App() {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
}

export default App;
